/**
 * IndexedDB-based PDF caching system
 *
 * Caches PDF documents in the browser to avoid repeated downloads from S3.
 * All documents in a workspace are cached with no expiration limit.
 */

const DB_NAME = 'ArgFinderPdfCache';
const DB_VERSION = 2; // Bumped to 2 to trigger schema upgrade (changed keyPath from 'url' to 'documentId')
const STORE_NAME = 'pdfs';

interface PdfCacheEntry {
  documentId: string;   // Document ID (used as primary key)
  workspaceId: string;  // Workspace ID for organization
  data: Blob;           // PDF binary data
  cachedAt: number;     // Timestamp when cached
  lastUrl: string;      // Last URL used (for reference only)
}

/**
 * Initialize IndexedDB database
 */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Delete old store if it exists (schema changed from v1 to v2)
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }

      // Create new object store with documentId as key
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'documentId' });

      // Create indexes for efficient queries
      store.createIndex('workspaceId', 'workspaceId', { unique: false });
      store.createIndex('cachedAt', 'cachedAt', { unique: false });
    };
  });
}

/**
 * Cache a PDF document
 */
export async function cachePdf(
  documentId: string,
  workspaceId: string,
  data: Blob,
  url?: string
): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const entry: PdfCacheEntry = {
      documentId,
      workspaceId,
      data,
      cachedAt: Date.now(),
      lastUrl: url || '',
    };

    const request = store.put(entry);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get a cached PDF document by document ID
 */
export async function getCachedPdf(documentId: string): Promise<Blob | null> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(documentId);

    request.onsuccess = () => {
      const entry = request.result as PdfCacheEntry | undefined;
      resolve(entry ? entry.data : null);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Check if a PDF is cached
 */
export async function isPdfCached(documentId: string): Promise<boolean> {
  const cached = await getCachedPdf(documentId);
  return cached !== null;
}

/**
 * Remove a specific PDF from cache
 */
export async function removeCachedPdf(documentId: string): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(documentId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove all PDFs for a specific workspace
 */
export async function clearWorkspacePdfs(workspaceId: string): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('workspaceId');
    const request = index.openCursor(IDBKeyRange.only(workspaceId));

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all cached PDFs
 */
export async function clearAllCachedPdfs(): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<{
  totalPdfs: number;
  totalSize: number;
  oldestCached: number | null;
  newestCached: number | null;
}> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const entries = request.result as PdfCacheEntry[];
      const totalPdfs = entries.length;
      const totalSize = entries.reduce((sum, entry) => sum + entry.data.size, 0);
      const timestamps = entries.map(e => e.cachedAt);

      resolve({
        totalPdfs,
        totalSize,
        oldestCached: timestamps.length > 0 ? Math.min(...timestamps) : null,
        newestCached: timestamps.length > 0 ? Math.max(...timestamps) : null,
      });
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Fetch and cache a PDF from a URL
 * Returns the Blob data
 */
export async function fetchAndCachePdf(
  documentId: string,
  workspaceId: string,
  url: string
): Promise<Blob> {
  if (documentId === "demo-document") {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.statusText}`);
    }
    return await response.blob();
  }

  // Check cache first (using document ID, not URL)
  const cached = await getCachedPdf(documentId);
  if (cached) {
    console.log(`PDF cache HIT for document ${documentId} (${(cached.size / 1024 / 1024).toFixed(2)} MB)`);
    return cached;
  }

  console.log(`PDF cache MISS for document ${documentId}, fetching from S3...`);

  // Fetch from URL
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch PDF: ${response.statusText}`);
  }

  const blob = await response.blob();

  // Cache for future use (using document ID as key)
  await cachePdf(documentId, workspaceId, blob, url);
  console.log(`Cached PDF for document ${documentId} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);

  return blob;
}
